"use client";
import {NeoSelect} from "@/components/forms";
import {NeoHeader} from "@/components/shared";
import {NeoAGgrid} from "@/components/table/neo-table/neo-ag-grid";
import {useNeoContext} from "@/context/neo/neo.context";
import {formatDateCommon, getTableConfigWithAccess} from "@/libs/helper";
import {ExpenseTableConfig} from "@/data/expensereports/table.config";
import {ExpenseService} from "@/service/expense.service";
import {SelectItem} from "@nextui-org/react";
import dynamic from "next/dynamic";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import {FaCalendarAlt} from "react-icons/fa";
import "./ChartTooltip.css";
import CircleProgress from "./circleprogress";
import {ExpenseReportsFilter} from "./expensereportsfilter";
const Chart = dynamic(() => import("react-apexcharts"), {ssr: false});

interface ChartData {
  options: any;
  series: ApexAxisChartSeries;
}

type Col = {uid: string; name: string};

interface DateRange {
  from: Date | null | undefined;
  to: Date | null | undefined;
}

export const ExpenseReports: React.FC = () => {
  const defaultsummary = {
    totalExpense: 0,
    fuelExpense: 0,
    maintenanceExpense: 0,
    miscExpense: 0
  };
  const defaultexpensepercentage = {
    fuel: 0,
    maintenance: 0,
    misc: 0
  };

  const {filterContext, offset, currency, roleAccess} = useNeoContext();
  const expenseConfig = getTableConfigWithAccess(roleAccess, ExpenseTableConfig, "MC008");
  const canWrite = expenseConfig.table.meta.hasCreate;
  const [loginUserId, setLoginUserId] = useState<string>("");
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [dataKey, setDataKey] = useState(0);
  const [filterKey, setFilterKey] = useState(0);
  const [summaryexpenses, setSummaryExpenses] = useState(defaultsummary);
  const [expensepercentages, setExpensePercentages] = useState(
    defaultexpensepercentage
  );
  const [isGroupSelectboxVisible, setGroupSelect] = useState(true);
  const [reportFilters, setReportFilters] = useState<any>();
  const [summaryRange, setSummaryRange] = useState<any>("2");
  const [chartseries, setChartSeries] = useState<any>([
    {
      name: "Total Expenses",
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    },
    {
      name: "Fuel",
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    },
    {
      name: "Maintenance",
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    },
    {
      name: "Miscellaneous",
      data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    }
  ]);
  const [chartcategories, setChartCategories] = useState<any>([
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar"
  ]);
  const [selectedFilterType, setSelectedFilterType] = useState<any>("1");
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(
    null
  );
  const [selectedVehicleIds, setVehicleIds] = useState<string[]>([]);
  const [summaryYear, setSummaryYears] = useState<string>("");
  const loginUserIdRef = useRef(loginUserId);
  const activeTabRef = useRef(activeTab);
  const offsetRef = useRef(offset);
  const reportFiltersRef = useRef(reportFilters);
  const reportSummaryRef = useRef(summaryRange);
  useEffect(() => {
    reportFiltersRef.current = reportFilters;
  }, [reportFilters]);

  const DEFAULT_COLUMNS = [
    {uid: "sno", name: "S.No"},
    {uid: "expenseRefId", name: "Expense RefId"},
    {uid: "vehicleNo", name: "Vehicle No"},
    {uid: "datestring", name: "Date"},
    {uid: "expenseName", name: "Expense Type"},
    {uid: "description", name: "Description / Type"},
    {uid: "amount", name: "Amount"},
    {uid: "documents", name: "Uploaded Files"}
  ];

  const BASE_ACTIONS = [
    {key: "view", label: "View", type: "modal", isVisible: false},
    {key: "edit", label: "Edit", type: "modal", isVisible: true},
    {key: "delete", label: "Delete", type: "modal", isVisible: true}
  ];
  const ALL_COLUMN_UIDS = DEFAULT_COLUMNS.map(c => c.uid);

  const tabs = [
    {id: "all", label: "All"},
    {id: "fuel", label: "Fuel Expense"},
    {id: "maintenance", label: "Maintenance"},
    {id: "misc", label: "Miscellaneous"}
  ] as const;

  const dateRanges = [
    {label: "Last 12 Months", value: "1"},
    {label: "This Financial Year", value: "2"},
    {label: "Last Financial Year", value: "3"}
  ];

  const FUEL_COLUMNS = [
    {uid: "sno", name: "S.No"},
    {uid: "expenseRefId", name: "Expense RefId"},
    {uid: "vehicle", name: "Vehicle No"},
    {uid: "datestring", name: "Date"},
    {uid: "fuelType", name: "Fuel Type"},
    {uid: "qty", name: "Qty"},
    {uid: "fuelRate", name: "Rate"},
    {uid: "totalCost", name: "Total Cost"},
    {uid: "fuelStation", name: "Fuel Station"},
    {uid: "openingKms", name: "Opening KMs"},
    {uid: "closingKms", name: "Closing KMs"},
    {uid: "totalKms", name: "Total Kms"},
    {uid: "mileage", name: "Consumed KMPL"},
    {uid: "documents", name: "Uploaded Files"},
    {uid: "actions", name: "Action"}
  ];

  const MAINTANCE_COLUMNS = [
    {uid: "sno", name: "S.No"},
    {uid: "expenseRefId", name: "Expense RefId"},
    {uid: "vehicle", name: "Vehicle No"},
    {uid: "datestring", name: "Date"},
    {uid: "serviceTypeName", name: "Service Type"},
    {uid: "totalCost", name: "Amount"},
    {uid: "vendorName", name: "Vendor"},
    {uid: "description", name: "Description"},
    {uid: "documents", name: "Uploaded Files"},
    {uid: "actions", name: "Action"}
  ];

  const MISCELLANEOUS_COLUMNS = [
    {uid: "sno", name: "S.No"},
    {uid: "expenseRefId", name: "Expense RefId"},
    {uid: "vehicle", name: "Vehicle No"},
    {uid: "datestring", name: "Date"},
    {uid: "expenseTypeName", name: "Expense Type"},
    {uid: "totalCost", name: "Amount"},
    {uid: "description", name: "Description"},
    {uid: "documents", name: "Uploaded Files"},
    {uid: "actions", name: "Action"}
  ];

  const chartDatas: ChartData = {
    series: chartseries,
    options: {
      chart: {type: "area", height: 350, toolbar: {show: false}},
      dataLabels: {enabled: false},
      stroke: {curve: "smooth", width: 2},
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.9,
          opacityTo: 0.4,
          stops: [0, 100]
        }
      },
      xaxis: {
        categories: chartcategories
      },
      yaxis: {
        labels: {
          formatter: (val: number) =>
            val > 1 ? `${(val / 1000).toFixed(0)}K` : `${val}K`
        }
      },
      tooltip: {
        shared: false,
        custom: ({series, seriesIndex, dataPointIndex, w}: any) => {
          const cur = w.config.neoCurrency ?? "₹";
          const seriesName = w.globals.seriesNames[seriesIndex];
          const value = series[seriesIndex][dataPointIndex].toLocaleString();
          return `<div class="custom-tooltip"><div class="title">${seriesName}</div><div class="value">${cur}${value}</div></div>`;
        }
      },
      colors: ["#1E90FF", "#FF7700", "#F7C94C", "#4CAF50"],
      legend: {position: "bottom", horizontalAlign: "center"},
      neoCurrency: currency
    }
  };
  const [chartData, setchartData] = useState(chartDatas);

  const replaceColumns = useCallback((newCols: Col[]) => {
    setTableConfigData((prev: any) => ({
      ...prev,
      columns: newCols.map(c => ({...c})),
      activeColumns: newCols.map(c => c.uid)
    }));
  }, []);

  const replaceRows = (rows: any) => {
    setTableConfigData((prev: any) => ({
      ...prev,
      tabledatas: Array.isArray(rows) ? rows : []
    }));
    setDataKey(k => k + 1);
  };

  const replaceActions = useCallback((newActions: any[]) => {
    setTableConfigData((prev: any) => ({
      ...prev,
      actions: newActions
    }));
  }, []);

  function calculatePercentages(expenses: any) {
    const {totalExpense, fuelExpense, maintenanceExpense, miscExpense} =
      expenses;

    if (totalExpense === 0) {
      return {fuel: 0, maintenance: 0, misc: 0};
    }

    return {
      fuel: ((fuelExpense / totalExpense) * 100).toFixed(1),
      maintenance: ((maintenanceExpense / totalExpense) * 100).toFixed(1),
      misc: ((miscExpense / totalExpense) * 100).toFixed(1)
    };
  }

  const getAcademicYear = (type: any) => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth();
    let startYear: number;
    let endYear: number;
    let from: Date;
    let to: Date;
    if (type == 1) {
      from = new Date(date);
      from.setMonth(from.getMonth() - 11);
      from.setHours(0, 0, 0, 0);
      to = new Date(date);
      to.setHours(23, 59, 59, 999);
    } else if (type == 2) {
      if (month >= 3) {
        startYear = year;
        endYear = year + 1;
      } else {
        startYear = year - 1;
        endYear = year;
      }
      from = new Date(startYear, 3, 1, 0, 0, 0);
      to = new Date(endYear, 2, 31, 23, 59, 59);
    } else if (type == 3) {
      if (month >= 3) {
        startYear = year - 1;
        endYear = year;
      } else {
        startYear = year - 2;
        endYear = year - 1;
      }
      from = new Date(startYear, 3, 1, 0, 0, 0);
      to = new Date(endYear, 2, 31, 23, 59, 59);
    } else {
      if (month >= 3) {
        startYear = year;
        endYear = year + 1;
      } else {
        startYear = year - 1;
        endYear = year;
      }
      from = new Date(startYear, 3, 1, 0, 0, 0);
      to = new Date(endYear, 2, 31, 23, 59, 59);
    }
    const years = from.getFullYear() + "-" + to.getFullYear();
    setSummaryYears(years);
    const returndata = {from: from, to: to};
    return returndata;
  };

  const gmtdefaultformat = (from: any, to: any) => {
    const fromdate =
      from instanceof Date
        ? Date.UTC(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 1)
        : undefined;

    const todate =
      to instanceof Date
        ? Date.UTC(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59)
        : undefined;
    const retrundata = {from: fromdate, to: todate};
    return retrundata;
  };
  useEffect(() => {
    onTabClick("all");
    if (filterContext?.client?.institutionUserId) {
      loginUserIdRef.current = filterContext.client.institutionUserId;
      offsetRef.current = offset;
      setLoginUserId(filterContext.client.institutionUserId);
      const academicYear = getAcademicYear(2);
      const gmttimestamp: any = gmtdefaultformat(
        academicYear?.from,
        academicYear?.to
      );
      const from = gmttimestamp.from;
      const to = gmttimestamp.to;
      const requestdata: any = {
        from: from,
        to: to,
        type: 1,
        filters: {
          branch: [loginUserIdRef.current],
          vehicleIds: []
        }
      };
      getexpensesummary(requestdata);
    }
  }, [filterContext]);

  const onTabClick = useCallback(
    (id: any) => {
      activeTabRef.current = id;
      let columns = DEFAULT_COLUMNS;
      if (id === "fuel") {
        columns = FUEL_COLUMNS;
        replaceActions(BASE_ACTIONS);
        setGroupSelect(false);
        setTableConfigData((prev: any) => ({
          ...prev,
          url: {
            ...prev.url,
            page: {
              add: "fueladd",
              edit: "fueledit"
            }
          },
          meta: {
            ...prev.meta,
            hasCreate: canWrite
          }
        }));
      } else if (id === "maintenance") {
        columns = MAINTANCE_COLUMNS;
        replaceActions(BASE_ACTIONS);
        setGroupSelect(false);
        setTableConfigData((prev: any) => ({
          ...prev,
          url: {
            ...prev.url,
            page: {
              add: "maintenanceadd",
              edit: "maintenanceedit"
            }
          },
          meta: {
            ...prev.meta,
            hasCreate: canWrite
          }
        }));
      } else if (id === "misc") {
        columns = MISCELLANEOUS_COLUMNS;
        replaceActions(BASE_ACTIONS);
        setGroupSelect(false);
        setTableConfigData((prev: any) => ({
          ...prev,
          url: {
            ...prev.url,
            page: {
              add: "miscellaneousadd",
              edit: "miscellaneousedit"
            }
          },
          meta: {
            ...prev.meta,
            hasCreate: canWrite
          }
        }));
      } else {
        columns = DEFAULT_COLUMNS;
        setTableConfigData((prev: any) => ({
          ...prev,
          url: {
            ...prev.url,
            page: {
              add: "expensesadd",
              edit: ""
            }
          },
          meta: {
            ...prev.meta,
            hasCreate: canWrite
          }
        }));
        setGroupSelect(true);
      }
      replaceColumns(columns);
      replaceRows([]);
      setActiveTab(id);
      setFilterKey(k => k + 1);
    },
    [replaceColumns, replaceRows, replaceActions]
  );

  // === API Loader ===
  /**
   * Loads data for the "All" tab with optional grouping:
   * groupId:
   * 0 -> Detailed rows (with documentsLength)
   * 1 -> Group by Vehicle
   * 2 -> Group by Date
   * 3 -> Group by Expense Type
   */
  const loadAllTab = useCallback(
    async (data: any) => {
      const vehicleIds = data?.vehicleIds ?? [];
      const groupId = Number(data?.groupId ?? 0);
      const range = data?.range;
      /*const from =
        range?.from instanceof Date ? range.from.getTime() : undefined;
      const to = range?.to instanceof Date ? range.to.getTime() : undefined;
      const offset = offsetRef.current;*/
      //const request: any = {from, to, offset, vehicleIds};
      const gmttimestamp: any = gmtdefaultformat(range?.from, range?.to);
      const request: any = {
        from: gmttimestamp.from,
        to: gmttimestamp.to,
        filters: {
          branch: [loginUserIdRef.current],
          vehicleIds: vehicleIds,
          expenseTypes: []
        }
      };
      let response: any;
      let cols: Col[] = [];
      if (groupId === 1) {
        cols = [
          {uid: "sno", name: "S.No"},
          {uid: "vehicleNo", name: "Vehicle No"},
          {uid: "total", name: "Total Expenses"},
          {uid: "fuel", name: "Fuel Expenses"},
          {uid: "maintenance", name: "Maintenance"},
          {uid: "misc", name: "Miscellaneous"}
        ];
        response = await ExpenseService.getgroupvehicle(
          loginUserIdRef.current,
          request
        );
      } else if (groupId === 2) {
        cols = [
          {uid: "sno", name: "S.No"},
          {uid: "datestring", name: "Date"},
          {uid: "total", name: "Total Expenses"},
          {uid: "fuel", name: "Fuel Expenses"},
          {uid: "maintenance", name: "Maintenance"},
          {uid: "misc", name: "Miscellaneous"}
        ];
        response = await ExpenseService.getgroupdate(
          loginUserIdRef.current,
          request
        );
      } else if (groupId === 3) {
        cols = [
          {uid: "sno", name: "S.No"},
          {uid: "expense", name: "Expense"},
          {uid: "total", name: "Total Expenses"},
          {uid: "vehiclesInvolved", name: "Vehicles Involved"},
          {uid: "maxExpense", name: "Max Expense"},
          {uid: "maxVehicleName", name: "Max Expense Vehicle"}
        ];
        response = await ExpenseService.getgroupexpense(
          loginUserIdRef.current,
          request
        );
      } else {
        cols = DEFAULT_COLUMNS;
        response = await ExpenseService.getoverallexpense(
          loginUserIdRef.current,
          request
        );
      }
      replaceColumns(cols);
      replaceRows([]);
      if (response?.data?.status === "success") {
        const rows = Array.isArray(response?.data?.data?.reportData)
          ? response.data.data?.reportData
          : [];
        {
          /*const tabledata =
          groupId === 0
            ? rows.map((item: any, index: number) => ({
                sno: index + 1,
                documentsLength:
                  item?.documents?.length && item.documents.length > 0
                    ? item.documents.length
                    : "",
                datestring: formatDateCommon(item?.date),
                total:Number(item?.total ?? 0).toFixed(2),
                ...item
              }))
            : rows.map((item: any, index: number) => ({
                sno: index + 1,
                datestring: formatDateCommon(item?.date),
                total:Number(item?.total ?? 0).toFixed(2),
                ...item
              }));*/
        }
        const tabledata = rows.map((item: any, index: number) => {
          const amount = Number(item?.amount ?? 0);
          let base = {
            ...item,
            sno: index + 1,
            datestring: formatDateCommon(item?.date),
            amount: amount.toFixed(2)
          };
          if (groupId == 1 || groupId == 2) {
            const total = Number(item?.total ?? 0);
            const fuel = Number(item?.fuel ?? 0);
            const maintenance = Number(item?.maintenance ?? 0);
            const misc = Number(item?.misc ?? 0);
            base = {
              ...item,
              sno: index + 1,
              datestring: formatDateCommon(item?.date),
              total: total.toFixed(2),
              fuel: fuel.toFixed(2),
              maintenance: maintenance.toFixed(2),
              misc: misc.toFixed(2)
            };
          }
          if (groupId == 3) {
            const total = Number(item?.total ?? 0);
            const maxExpense = Number(item?.maxExpense ?? 0);
            base = {
              ...item,
              sno: index + 1,
              datestring: formatDateCommon(item?.date),
              total: total.toFixed(2),
              maxExpense: maxExpense.toFixed(2)
            };
          }
          return groupId === 0
            ? {
                ...base,
                documentsLength: item?.documents?.length
                  ? item.documents.length
                  : ""
              }
            : base;
        });
        replaceRows(tabledata);
      } else {
        toast.error(response?.data?.message);
      }
    },
    [loginUserIdRef.current, offsetRef.current, replaceColumns, replaceRows]
  );

  const loadFuelTab = useCallback(
    async (data: any) => {
      const vehicleIds = data?.vehicleIds ?? [];
      const range = data?.range;
      //const from = range?.from instanceof Date ? range.from.getTime() : undefined;
      //const to = range?.to instanceof Date ? range.to.getTime() : undefined;
      const gmttimestamp: any = gmtdefaultformat(range?.from, range?.to);
      const from = gmttimestamp.from;
      const to = gmttimestamp.to;
      if (from && to) {
        const offset = offsetRef.current;
        //const request: any = {from, to, offset, vehicleIds};
        const request: any = {
          from: from,
          to: to,
          filters: {
            branch: [loginUserIdRef.current],
            vehicleIds: vehicleIds,
            expenseTypes: []
          }
        };
        const response = await ExpenseService.getfuelexpense(
          loginUserIdRef.current,
          request
        );
        if (response?.data?.status === "success") {
          const rows = Array.isArray(response?.data?.data?.reportData)
            ? response.data.data?.reportData
            : [];
          const tabledata = rows.map((item: any, index: number) => {
            const docsLen = item?.documents?.length ?? 0;
            const total = Number(item?.totalCost ?? 0);
            const fuelRate = Number(item?.fuelRate ?? 0);
            const qty = Number(item?.qty ?? 0);
            return {
              ...item, // first!
              sno: index + 1,
              documentsLength: docsLen > 0 ? docsLen : "",
              datestring: formatDateCommon(item?.date),
              totalCost: total.toFixed(2),
              fuelRate: fuelRate.toFixed(2),
              qty: qty.toFixed(2),
              reportType: 1
            };
          });
          replaceRows(tabledata);
        } else {
          toast.error(response?.data?.message);
        }
      }
    },
    [loginUserIdRef.current, offsetRef.current]
  );

  const loadMaintenanceTab = useCallback(
    async (data: any) => {
      const vehicleIds = data?.vehicleIds ?? [];
      const range = data?.range;
      //const from = range?.from instanceof Date ? range.from.getTime() : undefined;
      //const to = range?.to instanceof Date ? range.to.getTime() : undefined;
      const gmttimestamp: any = gmtdefaultformat(range?.from, range?.to);
      const from = gmttimestamp.from;
      const to = gmttimestamp.to;
      if (from && to) {
        const offset = offsetRef.current;
        //const request: any = {from, to, offset, vehicleIds};
        const request: any = {
          from: from,
          to: to,
          filters: {
            branch: [loginUserIdRef.current],
            vehicleIds: vehicleIds,
            expenseTypes: []
          }
        };
        const response = await ExpenseService.getmaintance(
          loginUserIdRef.current,
          request
        );
        if (response?.data?.status === "success") {
          const rows = Array.isArray(response?.data?.data?.reportData)
            ? response.data.data?.reportData
            : [];
          const tabledata = rows.map((item: any, index: number) => {
            const docsLen = item?.documents?.length ?? 0;
            const total = Number(item?.totalCost ?? 0);
            return {
              ...item,
              sno: index + 1,
              documentsLength: docsLen > 0 ? docsLen : "",
              datestring: formatDateCommon(item?.date),
              totalCost: total.toFixed(2),
              reportType: 2 //MAINTENANCE
            };
          });
          replaceRows(tabledata);
        } else {
          toast.error(response?.data?.message);
        }
      }
    },
    [loginUserIdRef.current, offsetRef.current]
  );

  const loadMiscTab = useCallback(
    async (data: any) => {
      const vehicleIds = data?.vehicleIds ?? [];
      const range = data?.range;
      //const from = range?.from instanceof Date ? range.from.getTime() : undefined;
      //const to = range?.to instanceof Date ? range.to.getTime() : undefined;
      const gmttimestamp: any = gmtdefaultformat(range?.from, range?.to);
      const from = gmttimestamp.from;
      const to = gmttimestamp.to;
      if (from && to) {
        const offset = offsetRef.current;
        //const request: any = {from, to, offset, vehicleIds};
        const request: any = {
          from: from,
          to: to,
          filters: {
            branch: [loginUserIdRef.current],
            vehicleIds: vehicleIds,
            expenseTypes: []
          }
        };
        const response = await ExpenseService.getmiscellaneous(
          loginUserIdRef.current,
          request
        );
        if (response?.data?.status === "success") {
          const rows = Array.isArray(response?.data?.data?.reportData)
            ? response.data.data?.reportData
            : [];

          const tabledata = rows.map((item: any, index: number) => {
            const docsLen = item?.documents?.length ?? 0;
            const total = Number(item?.totalCost ?? 0);
            return {
              ...item,
              sno: index + 1,
              documentsLength: docsLen > 0 ? docsLen : "",
              datestring: formatDateCommon(item?.date),
              totalCost: total.toFixed(2),
              reportType: 3 //MISCELLANEOUS
            };
          });

          replaceRows(tabledata);
        } else {
          toast.error(response?.data?.message);
        }
      }
    },
    [loginUserId, offsetRef.current]
  );

  const onFilterChange = useCallback(
    async (data: any) => {
      replaceRows([]);
      if (!loginUserIdRef.current) return;
      setSelectedFilterType(data.selectedFilterType);
      setSelectedDateRange(data.range);
      setVehicleIds(data.vehicleIds);
      // merge the new patch with current filters
      const nextFilters = {...reportFilters, ...data};
      setReportFilters(nextFilters);
      setLoginUserId(loginUserIdRef.current);
      //handleDateRangeSummary(summaryRange);
      switch (activeTabRef.current) {
        case "all":
          return await loadAllTab(nextFilters);
        case "fuel":
          return await loadFuelTab(nextFilters);
        case "maintenance":
          return await loadMaintenanceTab(nextFilters);
        case "misc":
          return await loadMiscTab(nextFilters);
        default:
          return;
      }
    },
    [
      activeTabRef.current,
      loginUserIdRef.current,
      loadAllTab,
      loadFuelTab,
      loadMaintenanceTab,
      loadMiscTab,
      replaceRows,
      reportFilters
    ]
  );

  const handleDateRangeSummary = (data: any) => {
    if (data) {
      reportSummaryRef.current = data;
      const academicYear = getAcademicYear(data);
      const gmttimestamp: any = gmtdefaultformat(
        academicYear?.from,
        academicYear?.to
      );
      const from = gmttimestamp.from;
      const to = gmttimestamp.to;
      const requestdata: any = {
        from: from,
        to: to,
        type: 1,
        //offSet: offsetRef.current
        filters: {
          branch: [loginUserIdRef.current],
          vehicleIds: []
        }
      };
      setSummaryRange(data);
      getexpensesummary(requestdata);
    } else {
      const allVals = chartseries.flatMap((s: any) => s.data as number[]);
      const dataMax = Math.max(0, ...allVals);
      const yMax =
        (dataMax === 0 ? 1000 : Math.ceil(dataMax / 1000) * 1000) + 1000;

      setchartData(prev => ({
        ...prev,
        series: chartseries,
        options: {
          ...prev.options,
          neoCurrency: currency,
          xaxis: {
            ...prev.options.xaxis,
            categories: chartcategories
          },
          yaxis: {
            min: 0,
            max: yMax,
            forceNiceScale: false,
            labels: {
              formatter: (val: number) => `${(val / 1000).toFixed(0)}K`
            }
          }
        }
      }));

      setSummaryExpenses(defaultsummary);
      setExpensePercentages(defaultexpensepercentage);
    }
  };

  const expensefilter = useCallback(() => {
    return onFilterChange(reportFiltersRef.current);
  }, [onFilterChange]);

  const expensesummaryfilter = useCallback(() => {
    return handleDateRangeSummary(reportSummaryRef.current);
  }, [handleDateRangeSummary]);

  const getexpensesummary = async (requestdata: any) => {
    setSummaryExpenses(defaultsummary);
    setExpensePercentages(defaultexpensepercentage);
    const response = await ExpenseService.getexpensesummary(
      loginUserIdRef.current,
      requestdata
    );
    if (
      response?.data?.status === "success" &&
      response?.data?.data?.reportData
    ) {
      const responsedata = response?.data?.data?.reportData;
      const summary = responsedata?.summary;
      const expensesPercentages = calculatePercentages(summary);
      setSummaryExpenses(prev => ({
        ...prev,
        ...summary
      }));
      setExpensePercentages((prev: any) => ({
        ...prev,
        ...expensesPercentages
      }));

      if (responsedata?.trends) {
        const trends = responsedata?.trends;
        const totalExpenses = trends.map((item: any) => item.totalExpense);
        const fuelExpenses = trends.map((item: any) => item.fuelExpense);
        const maintenanceExpenses = trends.map(
          (item: any) => item.maintenanceExpense
        );
        const miscExpenses = trends.map((item: any) => item.miscExpense);
        const months = trends.map((item: any) =>
          new Date(item.date).toLocaleString("default", {month: "short"})
        );

        const newSeries = [
          {name: "Total Expenses", data: totalExpenses},
          {name: "Fuel", data: fuelExpenses},
          {name: "Maintenance", data: maintenanceExpenses},
          {name: "Miscellaneous", data: miscExpenses}
        ];

        const allVals = newSeries.flatMap(s => s.data as number[]);
        const dataMax = Math.max(0, ...allVals);
        const yMax =
          (dataMax === 0 ? 1000 : Math.ceil(dataMax / 1000) * 1000) + 1000;

        setchartData(prev => ({
          ...prev,
          series: newSeries,
          options: {
            ...prev.options,
            neoCurrency: currency,
            xaxis: {
              ...prev.options.xaxis,
              categories: months
            },
            yaxis: {
              min: 0,
              max: yMax,
              forceNiceScale: false,
              labels: {
                formatter: (val: number) => `${(val / 1000).toFixed(0)}K`
              }
            }
          }
        }));
      }
    }
  };

  const baseTableConfig = useMemo(
    () => ({
      columns: DEFAULT_COLUMNS,
      activeColumns: ALL_COLUMN_UIDS,
      activeFilters: [],
      style: {height: "h-full"},
      meta: {
        id: 6,
        title: "",
        description: "Total Data",
        hasSearch: false,
        hasUpload: false,
        hasDownload: true,
        hasAdd: false,
        hasActions: false,
        hasCheckbox: false,
        hasFilter: false,
        hasFooter: true,
        hasHeader: true,
        hasPagination: true,
        hasCreate: canWrite
      },
      url: {page: {}, api: {}},
      actions: [],
      tabledatas: [],
      expensefilter: expensefilter,
      expensesummaryfilter: expensesummaryfilter
    }),
    [expensefilter, canWrite]
  );
  const [tableConfigData, setTableConfigData] = useState<any>(baseTableConfig);

  const gridKey = useMemo(
    () => (tableConfigData.columns || []).map((c: any) => c.uid).join("|"),
    [tableConfigData.columns]
  );

  const getLocale = () => {
    if (typeof navigator !== "undefined") {
      return (
        navigator.language ||
        (navigator.languages && navigator.languages[0]) ||
        "en-IN"
      );
    }
    return "en-IN"; // SSR / non-browser fallback
  };

  const formatAmountNumber = (num: number): string => {
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-5">
      <div className="mb-2 flex-row">
        <NeoHeader message="Expenses" />
      </div>
      {/* === Summary / Chart Section === */}
      <div className="bg-white p-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xl font-normal">
            Overview of Fleet Operational Costs
          </h3>
          <button
            className="-mt-2"
            onClick={() => setIsOpen(v => !v)}
            title={isOpen ? "Collapse" : "Expand"}
            aria-label={isOpen ? "Collapse details" : "Expand details"}
          >
            {isOpen ? "▲" : "▼"}
          </button>
        </div>
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"}`}
        >
          <div className="flex justify-end">
            <NeoSelect
              label=""
              id="daterange"
              placeholder="Please select type"
              className="w-60"
              startContent={<FaCalendarAlt className="text-[#1e2a44]" />}
              defaultSelectedKeys={["2"]}
              onChange={e => {
                const value = e.target.value;
                handleDateRangeSummary(value);
              }}
            >
              {dateRanges.map(e => (
                <SelectItem key={e.value} value={e.value}>
                  {e.label}
                </SelectItem>
              ))}
            </NeoSelect>
          </div>
          <div className="mt-2 grid grid-cols-12 gap-2">
            <div className="col-span-3 grid grid-cols-2 gap-2">
              <div className="m rounded-lg border p-2 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <img src="/images/cash.svg" alt="Total" className="h-4 w-4" />
                  <span className="text-sm font-medium text-gray-600">
                    Total
                  </span>
                </div>
                <p className="text-xl font-semibold text-gray-900">
                  <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                    {currency}
                    {formatAmountNumber(summaryexpenses.totalExpense)}
                  </span>
                </p>
              </div>
              <div className="rounded-lg border p-2 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <img
                    src="/images/fuel_yellow.svg"
                    alt="Fuel"
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium text-gray-600">
                    Fuel
                  </span>
                </div>
                <p className="text-xl font-semibold text-gray-900">
                  <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                    {currency}
                    {formatAmountNumber(summaryexpenses.fuelExpense)}
                  </span>
                </p>
              </div>
              <div className="rounded-lg border p-2 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <img
                    src="/images/service_yellow.svg"
                    alt="Maintenance"
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium text-gray-600">
                    Maintenance
                  </span>
                </div>
                <p className="text-xl font-semibold text-gray-900">
                  <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                    {currency}
                    {formatAmountNumber(summaryexpenses.maintenanceExpense)}
                  </span>
                </p>
              </div>
              <div className="rounded-lg border p-2 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <img
                    src="/images/miscellaneous_yellow.svg"
                    alt="Miscellaneous"
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium text-gray-600">
                    Miscellaneous
                  </span>
                </div>
                <p className="text-xl font-semibold text-gray-900">
                  <span className="overflow-wrap-anywhere block whitespace-normal break-words">
                    {currency}
                    {formatAmountNumber(summaryexpenses.miscExpense)}
                  </span>
                </p>
              </div>
            </div>

            <div className="col-span-6 rounded-lg border bg-white p-2 shadow-sm">
              <Chart
                options={chartData.options}
                series={chartData.series}
                type="area"
                height={200}
              />
              <h6 className="-mt-2 text-center font-medium">
                Summary for {summaryYear}
              </h6>
            </div>

            <div className="col-span-3 flex flex-col gap-4">
              <div className="flex items-center gap-4 rounded-lg border bg-white p-1 shadow-sm">
                <CircleProgress
                  value={expensepercentages.fuel}
                  icon={
                    <img
                      src="/images/fuel_FF7700.svg"
                      alt="Fuel"
                      className="h-5 w-5"
                    />
                  }
                  strokeColor="#FF7700"
                />
                <div>
                  <p className="text-xl font-semibold text-gray-900">
                    {expensepercentages.fuel}%
                  </p>
                  <p className="text-sm text-gray-600">Fuel Expenses</p>
                </div>
              </div>

              <div className="-mt-3 flex items-center gap-4 rounded-lg border bg-white p-1 shadow-sm">
                <CircleProgress
                  value={expensepercentages.maintenance}
                  icon={
                    <img
                      src="/images/service_yellow.svg"
                      alt="Service"
                      className="h-5 w-5"
                    />
                  }
                  strokeColor="#FFC727"
                />
                <div>
                  <p className="text-xl font-semibold text-gray-900">
                    {expensepercentages.maintenance}%
                  </p>
                  <p className="text-sm text-gray-600">Maintenance Expenses</p>
                </div>
              </div>

              <div className="-mt-3 flex items-center gap-4 rounded-lg border bg-white p-1 shadow-sm">
                <CircleProgress
                  value={expensepercentages.misc}
                  icon={
                    <img
                      src="/images/miscellaneous.svg"
                      alt="Service"
                      className="h-5 w-5"
                    />
                  }
                  strokeColor="#3DD598"
                />
                <div>
                  <p className="text-xl font-semibold text-gray-900">
                    {expensepercentages.misc}%
                  </p>
                  <p className="text-sm text-gray-600">
                    Miscellaneous Expenses
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>{" "}
      {/* /summary */}
      {/* === Tabs + Grid === */}
      <div className="mt-2 w-full bg-white p-2">
        <div className="flex gap-1 overflow-hidden rounded-xl border border-gray-200 p-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => onTabClick(tab.id)}
              className={`flex-1 rounded-xl py-2 text-sm font-medium transition-colors ${activeTab === tab.id ? "bg-black text-white" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="-ml-1 mt-1 rounded-md p-2">
          {/* Filter sits above one shared grid; loader changes by tab */}
          <ExpenseReportsFilter
            key={`${activeTab}-${filterKey}`}
            defaultFilterType={selectedFilterType}
            isGroupSelectboxVisible={isGroupSelectboxVisible}
            onFilterChange={onFilterChange}
            defaultselectedDateRange={selectedDateRange}
            defaultselectedVehicleIds={selectedVehicleIds}
          />

          {/* One grid only; swapping columns/rows via state */}
          <NeoAGgrid key={`${gridKey}-${dataKey}`} table={tableConfigData} />
        </div>
      </div>
    </div>
  );
};
